import React, { type JSX } from 'react';
import { Create } from '@mui/icons-material';
import CustomIcon from '../../components/CustomIcon';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { useDebounce, useRBAC } from '../../hooks';
import { useGetDeviceByIdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import {
    Container,
    IconButton,
    List,
    Stack,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Skeleton,
    Button,
} from '@mui/material';
import { ChannelValue, InfoListItem } from '@brightlayer-ui/react-components';
import EditNotifications from '@mui/icons-material/EditNotifications';
import InfoIcon from '@mui/icons-material/Info';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import { useTheme } from '@mui/material/styles';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { NotificationLog } from '@brightlayer-ui/icons-mui';
import { ListSkeleton } from 'pages/WidgetManagement/common/Skeletons';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { ThresholdModal } from './ThresholdModal';
import { useCustomDetailsApi, useHandleDeviceDetails } from './hooks';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { CustomTransComponent } from 'components';
import { CustomModal, ModalActions } from 'components/CustomModal';
import { useTransformData } from 'hooks/useTransformData';
import { InputWithCustomPlaceholder } from 'components/InputWithCustomPlaceholder';
import { subscribeTopic, unsubscribeTopic } from '@fiji/common/src/mqtt_connection/mqttConnection';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { useGetAllFeatureFlagQuery } from '@fiji/common/src/features/featureFlag/featureFlagApi';
import { useIconLibraryFlag } from '@fiji/common/src/hooks';
import { FeatureFlagData, UserProfile } from '@fiji/common/src/types';
import { ApiEndpointType, ApiResponseType } from '@fiji/common/src/features/common/commonTypes';
import {
    setMessageContent,
    setAppHeaderSubTitle,
    setAppHeaderTitle,
} from '@fiji/common/src/features/common/commonSlice';
import { useTranslation } from 'react-i18next';

export const DeviceDetails = (): JSX.Element => {
    const theme: any = useTheme();
    const dispatch = useAppDispatch();
    const [searchKey, setSearchKey] = React.useState<any>(undefined);
    const [infoModalData, setInfoModalData] = React.useState<{
        isOpen: boolean;
        title: string;
        description: string;
    }>({ isOpen: false, title: '', description: '' });
    const navigate = useNavigate();
    const { deviceId } = useParams();
    const thresholdModalRef = React.useRef<any>(null);
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const selectedNode = useTypedSelector((state) => state['common']['selectedNode']);
    const [, debouncedValue] = useDebounce(undefined, 300, searchKey);
    const { pointConversion } = useTransformData();

    const currentOrg = useTypedSelector(selectedOrg);
    const { t } = useTranslation();

    const { data: profileData } = useGetUserProfileQuery({}, { refetchOnMountOrArgChange: true }) as ApiEndpointType<
        ApiResponseType<UserProfile>
    >;

    const { data: deviceData, isLoading: isDeviceLoading } = useGetDeviceByIdQuery(deviceId);
    const [deviceDetails, { isLoading, updateDetails }] = useCustomDetailsApi(deviceId, debouncedValue);
    const { thresholdUpdateLoader, handleAllExpandCollapse, expandedCategories, setExpandedCategories } =
        useHandleDeviceDetails(deviceDetails);
    const mqttConnectionStatus = useTypedSelector((state) => state['common']['mqttConnectionStatus']);

    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canUpdateThreshold = hasPermission('device-details_edit-devices');
    const canManageThreshold = hasPermission('telemetry-thresholds_view-devices');
    const [featureflagValue, setFeatureflagValue] = React.useState({} as FeatureFlagData);
    const { isIconLibraryEnabled } = useIconLibraryFlag();
    const { data: featureFlagData }: any = useGetAllFeatureFlagQuery({});

    React.useEffect(() => {
        const featureFlag = featureFlagData?.data?.find(
            (x: FeatureFlagData) => x.featureFlagName.toLowerCase() === 'channelmanager'
        );
        if (featureFlag) {
            setFeatureflagValue(featureFlag);
        }
    }, [featureFlagData]);
    React.useEffect(() => {
        if (mqttConnectionStatus === true && profileData?.data && selectedNode?.groupId)
            subscribeTopic(
                `BSSRM/TREND/${profileData?.data?.currentRealmId}/${currentOrg?.id}/${selectedNode?.groupId}/${deviceId}/devices`,
                (message: any, topic: any) => updateDetails(message, topic)
            );
    }, [mqttConnectionStatus, selectedNode, profileData?.data]);

    const getIcon = (channel: any): JSX.Element => {
        const icon = channel?.icon;
        if (!icon?.URL && !icon?.name) {
            return <CustomIcon family={icon?.web?.family ?? ''} iconName={icon?.web?.name ?? ''} />;
        }
        if (icon?.isExternal === true) return <img src={icon?.URL} height={22} width={22} />;
        if (isIconLibraryEnabled && icon?.name && icon?.iconLibrary?.name)
            return <CustomIcon family={icon?.iconLibrary?.name} iconName={icon?.name} />;
        return <CustomIcon family={''} iconName={icon?.name} />;
    };
    React.useEffect(
        () => () => {
            unsubscribeTopic(
                `BSSRM/TREND/${profileData?.data?.currentRealmId}/${currentOrg?.id}/${selectedNode?.groupId}/${deviceId}/devices`
            );
        },
        []
    );
    React.useEffect(() => {
        if (!isDeviceLoading) {
            dispatch(setAppHeaderTitle(deviceData?.data?.name));
            dispatch(setAppHeaderSubTitle(deviceData?.data?.groupPath));
        }
        return (): any => {
            dispatch(setAppHeaderTitle(''));
            dispatch(setAppHeaderSubTitle(''));
        };
    }, [deviceData]);

    const getChannelValue = (channel: any): any => {
        if (channel?.enumDisplayName) {
            return channel?.enumDisplayName;
        } else if (channel?.enumName) {
            return channel?.enumName;
        }
        return channel?.value !== 'true' && channel?.value !== 'false'
            ? pointConversion(channel?.value)
            : channel?.value;
    };

    const shouldShowInfoIcon = (value: any): boolean => value.length > 32;

    const canShowThresholdIcon = (channel: any): boolean =>
        canManageThreshold &&
        channel?.type?.includes?.('Trend') &&
        channel?.dataType !== 'BOOLEAN' &&
        channel?.dataType !== 'STRING';

    const handleInfoIconClick = (title: string, description: string): void => {
        if (!description) return;
        setInfoModalData({
            isOpen: true,
            title: title ?? t('COMMON:DETAILS'),
            description,
        });
    };

    const handleCloseInfoModal = (): void => {
        setInfoModalData((prev) => ({ ...prev, isOpen: false }));
    };

    const handleCopyToClipboard = (): void => {
        if (infoModalData?.description && navigator?.clipboard?.writeText) {
            void navigator.clipboard.writeText(infoModalData.description).then(() => {
                dispatch(
                    setMessageContent({
                        isOpen: true,
                        message: t('COMMON:COPY_SUCCESS'),
                        duration: 3000,
                    })
                );
            });
        }
    };

    const infoModalActions: ModalActions[] = [
        {
            key: 'copy',
            label: 'Copy To Clipboard',
            variant: 'outlined',
            handleClick: handleCopyToClipboard,
        },
        {
            key: 'close',
            label: 'Close',
            variant: 'contained',
            close: true,
            handleClick: handleCloseInfoModal,
        },
    ];

    const renderDetailAccordions = (item: any): JSX.Element => (
        <Accordion
            key={item}
            expanded={expandedCategories[item] ?? true}
            onChange={(): void => {
                setExpandedCategories((prev: any) => ({ ...prev, [item]: !prev[item] }));
            }}
            defaultExpanded
        >
            <AccordionSummary
                expandIcon={<ExpandMore color="primary" />}
                aria-controls="panel1bh-content"
                id="expand"
                className="border-bottom-1"
            >
                <Typography
                    variant="body2"
                    fontWeight={'600'}
                    fontSize={'14px'}
                    sx={{ color: theme.palette?.primary?.main }}
                >
                    {item ?? '--'}
                </Typography>
            </AccordionSummary>
            <AccordionDetails className="padding-0">
                <List disablePadding>
                    {deviceDetails?.data[item]?.length > 0 ? (
                        deviceDetails?.data[item]?.map((channel: any) => {
                            const channelValue = getChannelValue(channel);
                            const showInfoIcon = shouldShowInfoIcon(channelValue);

                            return (
                                <InfoListItem
                                    key={channel?.channelId}
                                    icon={getIcon(channel)}
                                    divider="full"
                                    title={channel?.displayName}
                                    rightComponent={
                                        <Stack flexDirection={'row'} justifyContent={'space-between'}>
                                            {showInfoIcon ? (
                                                <IconButton
                                                    id="info"
                                                    onClick={(): void =>
                                                        handleInfoIconClick(
                                                            channel?.displayName ?? t('COMMON:DETAILS'),
                                                            channelValue as string
                                                        )
                                                    }
                                                >
                                                    <InfoIcon />
                                                </IconButton>
                                            ) : (
                                                <ChannelValue
                                                    unitSpace="show"
                                                    value={channelValue}
                                                    units={channel?.unit}
                                                />
                                            )}
                                            <Stack flexDirection={'row'}>
                                                {canShowThresholdIcon(channel) && (
                                                    <IconButton
                                                        id="threshold"
                                                        data-testid="threshold-btn"
                                                        disabled={selectedNode?.connectionStatus === 'Offline'}
                                                        onClick={(): void =>
                                                            thresholdModalRef?.current?.onClick(channel)
                                                        }
                                                    >
                                                        {canUpdateThreshold ? (
                                                            <EditNotifications />
                                                        ) : (
                                                            <NotificationLog />
                                                        )}
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        </Stack>
                                    }
                                />
                            );
                        })
                    ) : (
                        <InfoListItem
                            key={deviceDetails?.data[item]?.channelId}
                            icon={
                                <CustomIcon
                                    family={deviceDetails?.data[item]?.icon?.web?.family ?? ''}
                                    iconName={deviceDetails?.data[item]?.icon?.web?.name ?? ''}
                                />
                            }
                            divider="full"
                            title={deviceDetails?.data[item]?.displayName}
                            rightComponent={((): JSX.Element => {
                                const singleChannelValue = getChannelValue(deviceDetails?.data[item]);
                                const showInfoIcon = shouldShowInfoIcon(singleChannelValue);

                                return (
                                    <Stack flexDirection={'row'} justifyContent={'space-between'}>
                                        {showInfoIcon ? (
                                            <IconButton
                                                id="info"
                                                onClick={(): void =>
                                                    handleInfoIconClick(
                                                        deviceDetails?.data[item]?.displayName ?? t('COMMON:DETAILS'),
                                                        singleChannelValue as string
                                                    )
                                                }
                                            >
                                                <InfoIcon />
                                            </IconButton>
                                        ) : (
                                            <ChannelValue
                                                unitSpace="show"
                                                value={singleChannelValue}
                                                units={deviceDetails?.data[item]?.unit}
                                            />
                                        )}
                                        <Stack flexDirection={'row'}>
                                            {canShowThresholdIcon(deviceDetails?.data[item]) && (
                                                <IconButton
                                                    id="threshold"
                                                    data-testid="threshold-btn"
                                                    disabled={selectedNode?.connectionStatus === 'Offline'}
                                                    onClick={(): void =>
                                                        thresholdModalRef?.current?.onClick(deviceDetails?.data[item])
                                                    }
                                                >
                                                    {canUpdateThreshold ? <EditNotifications /> : <NotificationLog />}
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </Stack>
                                );
                            })()}
                        />
                    )}
                </List>
            </AccordionDetails>
        </Accordion>
    );

    const getAccordionContent = (): any => {
        if (isLoading || thresholdUpdateLoader) {
            return (
                <Accordion defaultExpanded>
                    <AccordionSummary
                        expandIcon={<ExpandMore color="primary" />}
                        aria-controls="panel1bh-content"
                        id="expand"
                        className="border-bottom-1"
                    >
                        <Skeleton animation="wave">
                            <Typography
                                variant="body2"
                                fontWeight={'600'}
                                fontSize={'14px'}
                                sx={{ color: theme.palette?.primary?.main }}
                            >
                                <CustomTransComponent translationKey={'COMMON:PRIORITY'} />
                            </Typography>
                        </Skeleton>
                    </AccordionSummary>
                    <AccordionDetails className="padding0">
                        <ListSkeleton total={10} customSize={25} customSx={{ height: '4.5rem' }} />
                    </AccordionDetails>
                </Accordion>
            );
        } else if (Object.keys(deviceDetails?.data ?? {})?.length) {
            return Object.keys(deviceDetails?.data)?.map((item: any) => renderDetailAccordions(item));
        }
        return <CustomEmptyState icon title={<CustomTransComponent translationKey={'COMMON:NO_DETAILS'} />} />;
    };

    return (
        <>
            <Container className="padding-y-36">
                <Stack mb={2} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Stack direction={'row'} alignItems={'center'} spacing={3}>
                        <InputWithCustomPlaceholder
                            id="searchKey"
                            value={searchKey ?? ''}
                            placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                                setSearchKey(e.target.value);
                            }}
                        />
                    </Stack>
                    <Stack direction={'row'} spacing={2} alignItems={'center'}>
                        <Button
                            id="toggleExpand"
                            data-testid="toggle-expand-btn"
                            sx={{
                                border: `1px solid ${theme?.palette?.primary?.main}`,
                                '&:hover': {
                                    backgroundColor: theme?.palette?.primary?.[50],
                                },
                            }}
                            variant="outlined"
                            onClick={handleAllExpandCollapse}
                            startIcon={<UnfoldMore />}
                        >
                            {Object?.values(expandedCategories)?.every((item: any) => item === true) ? (
                                <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:COLLAPSE_ALL'} />
                            ) : (
                                <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:EXPAND_ALL'} />
                            )}
                        </Button>
                        {featureflagValue?.enabled && (
                            <IconButton
                                id="edit-channel"
                                data-testid="edit-channel-btn"
                                aria-label="settings"
                                onClick={(): void => navigate(`/${currentRealmName}/channelSelector/${deviceId}`)}
                            >
                                <Create className="color-content" />
                            </IconButton>
                        )}
                    </Stack>
                </Stack>

                <Stack>{getAccordionContent()}</Stack>
            </Container>

            <ThresholdModal ref={thresholdModalRef} />
            <CustomModal
                type="small"
                header={infoModalData?.title}
                isOpen={infoModalData?.isOpen}
                actions={infoModalActions}
            >
                <Typography variant="body1" sx={{ maxHeight: '50vh', overflowY: 'auto', pr: 1 }}>
                    {infoModalData?.description}
                </Typography>
            </CustomModal>
        </>
    );
};
